import { useCallback, useEffect, useState } from "react";
import { ReviewsCard } from "@/components/admin/ReviewsCard";
import { StripeInfo } from "@/components/market/StripeInfo";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, ExternalLink, Loader2, Pencil, Trash2 } from "lucide-react";
import { api } from "@/lib/api";
import { useI18n } from "@/i18n/I18nContext";
import { translateError } from "@/i18n/errors";
import { PageHero } from "@/components/layout/PageHero";
import { AppIcon } from "@/components/apps/AppIcon";
import { vLabel } from "@/lib/version";
import { AppFormDialog } from "@/components/admin/AppFormDialog";
import { ApkUploader } from "@/components/admin/ApkUploader";
import { VersionList } from "@/components/admin/VersionList";
import { ConfirmButton } from "@/components/admin/ConfirmButton";
import { DescriptionDialog } from "@/components/admin/DescriptionDialog";
import { WarningBlock } from "@/components/market/WarningBlock";
import { useSpace } from "@/context/SpaceContext";
import { CategoryBadge, CategoryToggle } from "@/components/admin/CategoryToggle";
import { MinisiteEditor } from "@/components/admin/MinisiteEditor";
import { NoveltyEditor } from "@/components/admin/NoveltyEditor";
import { PlatformBadge } from "@/components/apps/Platform";
import { IncompleteAlert } from "@/components/dev/IncompleteAlert";
import { ReviewBadge, ReviewPanel } from "@/components/admin/ReviewPanel";

export default function AdminAppPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [app, setApp] = useState(null);
  const [versions, setVersions] = useState([]);
  const [editing, setEditing] = useState(() => new URLSearchParams(window.location.search).get("completer") === "1");
  const [descVersion, setDescVersion] = useState(null);
  const { t } = useI18n();
  const { base, root, publicBase, isDev } = useSpace();

  const load = useCallback(async () => {
    try {
      const [a, v] = await Promise.all([api.get(`${base}/apps/${id}`), api.get(`${base}/apps/${id}/versions`)]);
      setApp(a.data);
      setVersions(v.data);
    } catch {
      navigate(root, { replace: true });
    }
  }, [id, navigate, base, root]);

  useEffect(() => {
    load();
  }, [load]);

  // Refresh while APK analysis (icon + manifest) is running.
  const analyzing = versions.some((v) => v.tech_status === "pending");
  useEffect(() => {
    if (!analyzing) return undefined;
    const id = setTimeout(load, 3000);
    return () => clearTimeout(id);
  }, [analyzing, versions, load]);

  const remove = async () => {
    try {
      await api.delete(`${base}/apps/${id}`);
      toast.success(t("admin.appDeleted"));
      navigate(root, { replace: true });
    } catch (err) {
      toast.error(translateError(err, t));
    }
  };

  return (
    <div data-testid="admin-app-page">
      <PageHero eyebrow={t(isDev ? "dev.eyebrow" : "admin.eyebrow")} title={t("admin.manageTitle")}>
        <Link to={root} className="mt-4 inline-flex items-center gap-2 text-sm text-[var(--gold)] hover:text-[var(--gold-bright)]" data-testid="admin-back-link">
          <ArrowLeft className="h-4 w-4 rtl:-scale-x-100" /> {t("admin.dashboard")}
        </Link>
      </PageHero>
      {!app ? (
        <Loader2 className="mx-auto h-6 w-6 animate-spin text-[var(--gold)]" />
      ) : (
        <div className="mx-auto max-w-5xl space-y-6 px-4 sm:px-8">
          {isDev && <WarningBlock testId="dev-app-warning" />}
          <section className="metal-red flex flex-col flex-wrap gap-5 rounded-2xl p-6 sm:flex-row sm:items-center" data-testid="admin-app-info">
            <AppIcon app={app} size="h-20 w-20" px={80} />
            <div className="min-w-0 flex-1">
              <h2 className="break-words font-display text-lg font-black uppercase text-white" data-testid="admin-app-detail-name">{app.name}</h2>
              {app.active_version && <p className="mt-1 font-display text-sm text-[var(--gold-bright)]" data-testid="admin-app-detail-version">{t("detail.activeVersion", { v: vLabel(app.active_version) })}</p>}
              <div className="mt-1 flex flex-wrap gap-2"><CategoryBadge app={app} /><PlatformBadge app={app} testId="admin-app-detail-platform" />{!isDev && app.review && <ReviewBadge status={app.review} testId="admin-app-review-badge" />}</div>
              {app.platform?.incomplete && <div className="mt-2"><IncompleteAlert testId="admin-app-detail-incomplete" /></div>}
              <p className="mt-1 line-clamp-3 break-words [overflow-wrap:anywhere] text-sm text-red-100/80" data-testid="admin-app-detail-description">{app.description || t("detail.noDescription")}</p>
            </div>
            <div className="flex flex-wrap gap-2">
              <CategoryToggle app={app} onChanged={setApp} />
              <Link to={`${publicBase}/${app.id}`} className="btn-gold btn-sm" data-testid="admin-view-public-link"><ExternalLink className="h-4 w-4" /> {t("admin.publicPage")}</Link>
              <button type="button" className="btn-gold btn-sm" onClick={() => setEditing(true)} data-testid="admin-edit-app-btn"><Pencil className="h-4 w-4" /> {t("admin.edit")}</button>
              <ConfirmButton
                title={t("admin.deleteAppTitle")}
                description={t("admin.deleteAppDesc")}
                onConfirm={remove}
                className="btn-red btn-sm"
                testId="admin-delete-app-btn"
              >
                <Trash2 className="h-4 w-4" /> {t("admin.delete")}
              </ConfirmButton>
            </div>
          </section>
          {!isDev && <ReviewPanel key={`rev-${app.id}-${versions.length}`} appId={app.id} onChanged={load} />}
          {!isDev && app.category === "minisite" && <MinisiteEditor key={app.id} app={app} onSaved={setApp} />}
          {!isDev && <NoveltyEditor key={`nov-${app.id}`} app={app} />}
          {isDev && <StripeInfo dismissible testId="dev-app-upload-stripe-info" />}
          <ApkUploader appId={app.id} onDone={(v) => { load(); setDescVersion(v); }} />
          <VersionList versions={versions} onChanged={load} onEditDescription={setDescVersion} />
          <ReviewsCard appId={app.id} title={t("reviews.appTitle")} />
        </div>
      )}
      {descVersion && (
        <DescriptionDialog
          version={versions.find((x) => x.id === descVersion.id) || descVersion}
          onClose={() => { setDescVersion(null); load(); }}
          onPublished={load}
        />
      )}
      {editing && app && <AppFormDialog open={editing} onOpenChange={setEditing} app={app} onSaved={load} />}
    </div>
  );
}
